// =============================================================================
// Shared Kapio currency formatters — single source of truth for all four apps
// (mobile, kapio-admin, kapio-desktop, kapio-landing). Each app's local
// utility file re-exports from here with backwards-compat aliases so
// existing call sites don't change.
//
// All DB prices are integers in Ngwee (1 Kwacha = 100 Ngwee). Always divide
// by 100 for display. Never use heuristics like `n >= 100 ? n / 100 : n`.
//
// Naming convention (use canonical names in new code):
//   fmtKwacha         "K500.00"          always 2 decimals, no thousands sep
//   fmtKwachaLocale   "K1,234.56"        locale separators + always 2 decimals
//   fmtKwachaCompact  "K500.5" / "K1,234" locale, preserves whatever decimals exist
//   fmtKwachaShort    "K500" / "K1.2k"   abbreviated for >=1000, no decimals
//   fmtKwachaAbbr     "K500" / "K1.2M"   rounded, with M suffix for >=1M (KPI cards)
// =============================================================================

/** Always 2 decimals, no thousands separator. "K500.00" / "K1234.56". */
export function fmtKwacha(ngwee) {
  if (ngwee == null || Number.isNaN(ngwee)) return "K0.00";
  return `K${(ngwee / 100).toFixed(2)}`;
}

/** Locale-formatted with always 2 decimals. "K1,234.56". Price-tag style. */
export function fmtKwachaLocale(ngwee) {
  if (ngwee == null || Number.isNaN(ngwee)) return "K0.00";
  return `K${(ngwee / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Locale-formatted, preserves natural decimals. "K500.5" / "K1,234.56". */
export function fmtKwachaCompact(ngwee) {
  return `K${((ngwee || 0) / 100).toLocaleString()}`;
}

/** Compact short form. "K500" / "K1.2k" / "K1234.56" rounded to whole. */
export function fmtKwachaShort(ngwee) {
  if (ngwee == null || Number.isNaN(ngwee)) return "K0";
  const kwacha = ngwee / 100;
  if (kwacha >= 1000) return `K${(kwacha / 1000).toFixed(1)}k`;
  return `K${kwacha.toFixed(0)}`;
}

/** Stats / chart axes. Rounds, switches to M for >=1M. "K500" / "K1,234" / "K1.2M". */
export function fmtKwachaAbbr(ngwee) {
  const k = (ngwee || 0) / 100;
  if (k >= 1_000_000) return `K${(k / 1_000_000).toFixed(1)}M`;
  if (k >= 1_000)     return `K${Math.round(k).toLocaleString()}`;
  return `K${Math.round(k)}`;
}

/** Convert kwacha (float) → ngwee (integer, rounded). */
export function toNgwee(kwacha) {
  return Math.round((kwacha || 0) * 100);
}

/** Convert ngwee (integer) → kwacha (float). */
export function fromNgwee(ngwee) {
  return (ngwee || 0) / 100;
}

/**
 * Calculate platform commission and seller payout from a total in ngwee.
 * Default rate is 10% (Kapio's platform commission).
 * Returns both values in ngwee.
 */
export function calculateCommission(totalNgwee, rate = 0.10) {
  const commission = Math.round((totalNgwee || 0) * rate);
  return { commission, payout: (totalNgwee || 0) - commission };
}

/**
 * Format a price range from two ngwee values: "K10.00 – K50.00".
 * If min === max, just returns one price.
 */
export function formatPriceRange(minNgwee, maxNgwee) {
  if (minNgwee == null || maxNgwee == null) return "";
  if (minNgwee === maxNgwee) return fmtKwacha(minNgwee);
  return `${fmtKwacha(minNgwee)} – ${fmtKwacha(maxNgwee)}`;
}
